/**
 * Projetos — o padrão 01, lista de trabalho.
 *
 * É a porta de entrada: sem abrir um projeto não há `projetoId`, e sem
 * `projetoId` não há pinout, cadastro nem diagrama. Por isso ela é simples de
 * propósito — filtro no cabeçalho, visões salvas, e Enter abrindo o projeto.
 *
 * A busca vai para o servidor (a API pagina em 200 e há 921 projetos) e casa
 * na descrição ou no número, por pedaço, sem caixa e sem acento: "hb20" acha
 * "HB20 1.0", "812" acha o projeto 812. Aplica sozinha quando a digitação para
 * — 2 caracteres e 400 ms, a regra 2; número vale com qualquer tamanho — e o
 * Enter aplica na hora.
 *
 * Os filtros de **marca**, **modelo** e **motor** acham os projetos que têm algum
 * modelo daquele veículo/motorização (eles moram no modelo, não no projeto) — os
 * de veículo e motor são os do sistema antigo. Com marca escolhida, a lista de
 * modelos mostra só os dela.
 */

import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import { BUSCA_ESPERA_MS, BUSCA_MINIMO_CARACTERES, chaves, POR_PAGINA } from "@/api/query"
import { ErroDaApi } from "@/api/erros"
import { MOSTRAR_GRAFICO_DESATUALIZADO, type PaginaResponse, type ProjetoResponse } from "@/api/tipos"
import { Faixa } from "@/kit/Faixa"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { LinhaDeAviso, Tabela, type Coluna } from "@/kit/Tabela"
import { SeletorDeVisoes } from "@/kit/SeletorDeVisoes"
import { SeletorComBusca } from "@/kit/SeletorComBusca"
import { contarFiltros, visaoPadrao, type Visao } from "@/kit/visoes"
import { NovoProjeto } from "./projeto/NovoProjeto"
import { CATEGORIAS } from "./projeto/categorias"
import { usarNomes } from "./outros/pecas"
import { usarSessao } from "@/sessao/Sessao"

const TELA = "projetos"


type FiltroProjetos = {
  descricao: string
  categoria: string
  /** 0 = qualquer marca. */
  marcaId: number
  /** O modelo do veículo (`veiculo` no cadastro). 0 = qualquer um. */
  veiculoId: number
  /** 0 = qualquer motor. */
  motorId: number
  /** "todos" | "atualizar" | "concluidos" | "abertos" — filtro local, sem ida ao banco. */
  estado: string
}

const VAZIO: FiltroProjetos = { descricao: "", categoria: "", marcaId: 0, veiculoId: 0, motorId: 0, estado: "todos" }

export function Projetos() {
  const navegar = useNavigate()
  const { pode } = usarSessao()
  // `?novo=1` abre o formulário — é como o menu "Projetos" do topo chega aqui.
  const [busca, setBusca] = useSearchParams()
  const incluindo = busca.get("novo") === "1" && pode("projeto_editar")
  const incluir = (sim: boolean) => setBusca(sim ? { novo: "1" } : {}, { replace: true })
  const inicial = useMemo(() => visaoPadrao<FiltroProjetos>(TELA, "geral"), [])

  // Visão salva antes dos filtros de marca, modelo e motor não tem esses campos: completa com o vazio.
  const [filtro, setFiltro] = useState<FiltroProjetos>({ ...VAZIO, ...inicial?.filtro })
  const [rascunho, setRascunho] = useState(filtro.descricao)
  const [visaoAtiva, setVisaoAtiva] = useState<string | null>(inicial?.id ?? null)
  const [pagina, setPagina] = useState(1)

  const { data, isPending, isFetching, error } = useQuery({
    queryKey: chaves.projetos({
      d: filtro.descricao, c: filtro.categoria, ma: filtro.marcaId, v: filtro.veiculoId, m: filtro.motorId, p: pagina,
    }),
    queryFn: () =>
      obter<PaginaResponse<ProjetoResponse>>("api/projetos", {
        descricao: filtro.descricao.trim() || undefined,
        categoria: filtro.categoria || undefined,
        marcaId: filtro.marcaId || undefined,
        veiculoId: filtro.veiculoId || undefined,
        motorId: filtro.motorId || undefined,
        pagina,
      }),
  })

  // `estado` é filtro local: os três valores saem de campos que já vieram na
  // resposta, então filtrar por eles não custa consulta nenhuma.
  const itens = useMemo(() => {
    const todos = data?.itens ?? []
    switch (filtro.estado) {
      // Escondido o aviso, o filtro também some — e uma visão salva com ele não esconde linha calada.
      case "atualizar": return MOSTRAR_GRAFICO_DESATUALIZADO ? todos.filter((p) => p.atualizar) : todos
      case "concluidos": return todos.filter((p) => p.concluido === 1)
      case "abertos": return todos.filter((p) => p.concluido !== 1)
      default: return todos
    }
  }, [data, filtro.estado])

  // A digitação parou: aplica o que está no campo. Apagar tudo também aplica (volta a lista inteira).
  useEffect(() => {
    const termo = rascunho.trim()
    if (termo === filtro.descricao.trim()) return
    if (termo !== "" && termo.length < BUSCA_MINIMO_CARACTERES && !/^\d+$/.test(termo)) return
    const t = setTimeout(() => aplicar({ ...filtro, descricao: rascunho }), BUSCA_ESPERA_MS)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rascunho])

  const total = data?.total ?? 0
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA))
  const quantos = contarFiltros({
    ...filtro, marcaId: filtro.marcaId || "", veiculoId: filtro.veiculoId || "",
    motorId: filtro.motorId || "", estado: filtro.estado === "todos" ? "" : filtro.estado,
  })

  const nomes = usarNomes()
  const marcas = useMemo(() => opcoes(nomes.marca), [nomes.marca])
  const modelos = useMemo(() => opcoes(nomes.veiculo,
    (id) => !filtro.marcaId || nomes.marcaDoVeiculo.get(id) === filtro.marcaId),
  [nomes.veiculo, nomes.marcaDoVeiculo, filtro.marcaId])
  const motores = useMemo(() => opcoes(nomes.motor), [nomes.motor])

  function aplicar(f: FiltroProjetos, visao: string | null = null) {
    setFiltro(f)
    setRascunho(f.descricao)
    setVisaoAtiva(visao)
    setPagina(1)
  }

  const colunas: Coluna<ProjetoResponse>[] = [
    { chave: "id", rotulo: "Projeto", largura: 78, mono: true, alinhamento: "direita", render: (p) => p.id },
    {
      chave: "descricao",
      rotulo: "Descrição",
      minimo: 300,
      render: (p) => (
        <>
          {p.descricao}
          {p.concluido === 1 && (
            <span style={{ marginLeft: 8, fontSize: 10.5, color: "var(--ok-on-soft)", fontWeight: 600 }}>
              concluído
            </span>
          )}
        </>
      ),
    },
    { chave: "categoria", rotulo: "Categoria", largura: 130, render: (p) => p.categoria },
    {
      chave: "atualizar",
      rotulo: "Estado",
      largura: 168,
      render: (p) =>
        p.atualizar ? (
          <span
            title="RN-05: houve escrita de pinout depois da última geração. O gráfico e o diagrama estão velhos."
            style={{
              display: "inline-flex", alignItems: "center", gap: 5, height: 18, padding: "0 7px",
              borderRadius: 5, background: "var(--acao-soft)", color: "var(--acao-on-soft)",
              fontSize: 10.5, fontWeight: 600,
            }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true"
                 style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" }}>
              <path d="M12 4 2.8 20h18.4L12 4Z" /><path d="M12 10.2v4M12 17.2v.4" />
            </svg>
            gráfico desatualizado
          </span>
        ) : null,
    },
    {
      chave: "data",
      rotulo: "Atualizado",
      largura: 104,
      mono: true,
      alinhamento: "direita",
      render: (p) => (p.dataAtualizacao ? new Date(p.dataAtualizacao).toLocaleDateString("pt-BR") : ""),
    },
  ]

  return (
    <>
      <Faixa titulo="Projetos" detalhe={total > 0 ? `${total.toLocaleString("pt-BR")} no total` : undefined}>
        <Busca
          valor={rascunho}
          aoMudar={setRascunho}
          aoAplicar={() => aplicar({ ...filtro, descricao: rascunho })}
          aoLimpar={() => aplicar({ ...filtro, descricao: "" })}
        />
        {pode("projeto_editar") && !incluindo && (
          <button
            onClick={() => incluir(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px",
              borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: 0,
              background: "var(--brand)", color: "#FFFFFF", cursor: "pointer", flex: "0 0 auto",
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"
                 style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round" }}>
              <path d="M12 5v14M5 12h14" />
            </svg>
            Novo projeto
          </button>
        )}
      </Faixa>

      {incluindo && <NovoProjeto aoFechar={() => incluir(false)} />}

      <div
        className="barra"
        style={{
          height: 38, flex: "0 0 38px", display: "flex", alignItems: "center", gap: 10,
          padding: "0 18px", background: "var(--surface2)", borderBottom: "1px solid var(--line2)",
        }}
      >
        <SeletorDeVisoes<FiltroProjetos>
          tela={TELA}
          escopo="geral"
          filtroAtual={filtro}
          temFiltro={quantos}
          ativa={visaoAtiva}
          aoAplicar={(v: Visao<FiltroProjetos>) => aplicar({ ...VAZIO, ...v.filtro }, v.id)}
          aoLimpar={() => aplicar(VAZIO)}
        />

        <span style={{ width: 1, height: 18, background: "var(--line2)", margin: "0 2px" }} />

        <Escolha
          rotulo="Categoria"
          opcoes={[["", "Todas"], ...CATEGORIAS.map((c) => [c, c] as [string, string])]}
          valor={filtro.categoria}
          aoMudar={(v) => aplicar({ ...filtro, categoria: v })}
        />
        <div style={{ width: 180, flex: "0 1 180px", minWidth: 120 }}>
          <SeletorComBusca
            itens={marcas}
            valor={filtro.marcaId}
            // Modelo de outra marca deixa de valer: a lista dele some, e o filtro junto.
            aoMudar={(v) => aplicar({
              ...filtro, marcaId: v,
              veiculoId: v && nomes.marcaDoVeiculo.get(filtro.veiculoId) !== v ? 0 : filtro.veiculoId,
            })}
            carregando={nomes.carregando}
            rotuloVazio="Todas as marcas"
            placeholder="Marca: digite para procurar…"
            rotuloAcessivel="Marca"
            maximoDesenhado={Number.POSITIVE_INFINITY}
          />
        </div>
        <div style={{ width: 220, flex: "0 1 220px", minWidth: 140 }}>
          <SeletorComBusca
            itens={modelos}
            valor={filtro.veiculoId}
            aoMudar={(v) => aplicar({ ...filtro, veiculoId: v })}
            carregando={nomes.carregando}
            rotuloVazio="Todos os modelos"
            placeholder="Modelo: digite para procurar…"
            rotuloAcessivel="Modelo"
            maximoDesenhado={Number.POSITIVE_INFINITY}
          />
        </div>
        <div style={{ width: 240, flex: "0 1 240px", minWidth: 140 }}>
          <SeletorComBusca
            itens={motores}
            valor={filtro.motorId}
            aoMudar={(v) => aplicar({ ...filtro, motorId: v })}
            carregando={nomes.carregando}
            rotuloVazio="Todos os motores"
            placeholder="Motor: digite para procurar…"
            rotuloAcessivel="Motor"
            maximoDesenhado={Number.POSITIVE_INFINITY}
          />
        </div>
        <Escolha
          rotulo="Estado"
          opcoes={[
            ["todos", "Todos"],
            ...(MOSTRAR_GRAFICO_DESATUALIZADO ? [["atualizar", "Desatualizados"] as [string, string]] : []),
            ["abertos", "Em aberto"], ["concluidos", "Concluídos"],
          ]}
          valor={filtro.estado}
          aoMudar={(v) => aplicar({ ...filtro, estado: v })}
        />

        {quantos > 0 && (
          <button onClick={() => aplicar(VAZIO)} style={{ fontSize: 11.5, color: "var(--txt3)" }}>
            limpar {quantos} {quantos === 1 ? "filtro" : "filtros"}
          </button>
        )}
      </div>

      <Tabela<ProjetoResponse>
        colunas={MOSTRAR_GRAFICO_DESATUALIZADO ? colunas : colunas.filter((c) => c.chave !== "atualizar")}
        itens={itens}
        chaveDe={(p) => p.id}
        aoSelecionar={(p) => navegar(`/projetos/${p.id}/pinout`)}
        vazio={
          isPending ? (
            <LinhaDeAviso>Carregando os projetos…</LinhaDeAviso>
          ) : error ? (
            <LinhaDeAviso>
              <b style={{ color: "var(--erro)" }}>
                {error instanceof ErroDaApi ? error.message : "Não foi possível falar com o servidor."}
              </b>
            </LinhaDeAviso>
          ) : quantos > 0 ? (
            <LinhaDeAviso>
              Nenhum projeto com {quantos === 1 ? "esse filtro" : "esses filtros"}.
              <button onClick={() => aplicar(VAZIO)}
                      style={{ fontSize: 12.5, fontWeight: 600, color: "var(--brand-on-soft)" }}>
                Limpar e ver todos
              </button>
            </LinhaDeAviso>
          ) : (
            <LinhaDeAviso>Nenhum projeto.</LinhaDeAviso>
          )
        }
      />

      <BarraDeEstado
        direita={
          <>
            {isFetching && <span style={{ color: "var(--txt3)" }}>carregando…</span>}
            {paginas > 1 && (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Passo ligado={pagina > 1} rotulo="Página anterior" aoIr={() => setPagina(pagina - 1)} seta="m14.5 6-6 6 6 6" />
                <span className="num" style={{ minWidth: 62, textAlign: "center" }}>{pagina} de {paginas}</span>
                <Passo ligado={pagina < paginas} rotulo="Próxima página" aoIr={() => setPagina(pagina + 1)} seta="m9.5 6 6 6-6 6" />
              </span>
            )}
          </>
        }
      >
        <span>
          <b className="num" style={{ color: "var(--txt)" }}>{itens.length}</b>
          {filtro.estado !== "todos" && data && ` de ${data.itens.length} nesta página`}
          {filtro.estado === "todos" && total > 0 && ` de ${total.toLocaleString("pt-BR")}`} projetos
        </span>
        <span style={{ color: "var(--txt3)" }}>Enter numa linha abre o pinout</span>
      </BarraDeEstado>
    </>
  )
}

/** Um catálogo id → nome como opções do seletor, em ordem alfabética e sem os vazios. */
function opcoes(nomes: Map<number, string>, manter: (id: number) => boolean = () => true) {
  return [...nomes.entries()]
    .filter(([id, nome]) => id > 0 && nome.trim() && manter(id))
    .sort((a, b) => a[1].localeCompare(b[1], "pt-BR"))
    .map(([id, nome]) => ({ id, rotulo: nome }))
}

function Busca({ valor, aoMudar, aoAplicar, aoLimpar }: {
  valor: string; aoMudar: (v: string) => void; aoAplicar: () => void; aoLimpar: () => void
}) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 7, height: 30, padding: "0 10px",
        borderRadius: 7, background: "var(--surface2)", border: "1px solid var(--line2)",
        flex: "1 1 200px", maxWidth: 290, minWidth: 0,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"
           style={{ flex: "0 0 14px", fill: "none", stroke: "var(--txt3)", strokeWidth: 1.9, strokeLinecap: "round" }}>
        <circle cx="11" cy="11" r="7" /><path d="m20 20-3.7-3.7" />
      </svg>
      <input
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") aoAplicar()
          if (e.key === "Escape") { aoMudar(""); aoLimpar() }
        }}
        placeholder="Buscar por descrição ou número"
        aria-label="Buscar projeto"
        style={{ width: "100%", minWidth: 120, border: 0, outline: "none", background: "transparent", fontSize: 12.5 }}
      />
      {valor && (
        <button onClick={() => { aoMudar(""); aoLimpar() }} aria-label="Limpar a busca"
                style={{ display: "flex", color: "var(--txt3)" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
               style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round" }}>
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
      )}
    </div>
  )
}

function Escolha({ rotulo, opcoes, valor, aoMudar }: {
  rotulo: string; opcoes: [string, string][]; valor: string; aoMudar: (v: string) => void
}) {
  const ativo = valor !== "" && valor !== "todos"
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span className="so-leitor">{rotulo}</span>
      <select
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        style={{
          height: 28, padding: "0 8px", borderRadius: 7, fontSize: 12,
          background: ativo ? "var(--brand-soft)" : "var(--surface)",
          color: ativo ? "var(--brand-on-soft)" : "var(--txt2)",
          border: `1px solid ${ativo ? "var(--brand)" : "var(--line2)"}`,
          fontWeight: ativo ? 600 : 400,
        }}
      >
        {opcoes.map(([v, r]) => <option key={v} value={v}>{v === "" || v === "todos" ? rotulo + ": " + r : r}</option>)}
      </select>
    </label>
  )
}

function Passo({ ligado, rotulo, aoIr, seta }: {
  ligado: boolean; rotulo: string; aoIr: () => void; seta: string
}) {
  return (
    <button
      disabled={!ligado}
      onClick={aoIr}
      aria-label={rotulo}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: 5, border: "1px solid var(--line2)",
        background: "var(--surface)", color: "var(--txt2)",
        opacity: ligado ? 1 : 0.4, cursor: ligado ? "pointer" : "default",
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
           style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" }}>
        <path d={seta} />
      </svg>
    </button>
  )
}
